import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { setStartTime, setEndTime, getPlaybackEvents, setScrollerObj, setSelectedEvent } from '../../actions';

const Timeline = (props) => {
  const hourList = [
    {time: '000000', ts: '00:00:00', label: '12am'},
    {time: '010000', ts: '01:00:00', label: '1am'},
    {time: '020000', ts: '02:00:00', label: '2am'},
    {time: '030000', ts: '03:00:00', label: '3am'},
    {time: '040000', ts: '04:00:00', label: '4am'},
    {time: '050000', ts: '05:00:00', label: '5am'},
    {time: '060000', ts: '06:00:00', label: '6am'},
    {time: '070000', ts: '07:00:00', label: '7am'},
    {time: '080000', ts: '08:00:00', label: '8am'},
    {time: '090000', ts: '09:00:00', label: '9am'},
    {time: '100000', ts: '10:00:00', label: '10am'},
    {time: '110000', ts: '11:00:00', label: '11am'},
    {time: '120000', ts: '12:00:00', label: '12pm'},
    {time: '130000', ts: '13:00:00', label: '1pm'},
    {time: '140000', ts: '14:00:00', label: '2pm'},
    {time: '150000', ts: '15:00:00', label: '3pm'},
    {time: '160000', ts: '16:00:00', label: '4pm'},
    {time: '170000', ts: '17:00:00', label: '5pm'},
    {time: '180000', ts: '18:00:00', label: '6pm'},
    {time: '190000', ts: '19:00:00', label: '7pm'},
    {time: '200000', ts: '20:00:00', label: '8pm'},
    {time: '210000', ts: '21:00:00', label: '9pm'},
    {time: '220000', ts: '22:00:00', label: '10pm'},
    {time: '230000', ts: '23:00:00', label: '11pm'},
    {time: '240000', ts: '24:00:00', label: '12mn'},
  ]
  const date = (props.searchFilters && props.searchFilters.date) ? props.searchFilters.date : props.date
  const scrollRef = useRef(null)
  const [scrollTimeout, setScrollTimeout] = useState(null)

  // eslint-disable-next-line
  const [initialScrollRef, setInitialScrollRef] = useState(null)

  useEffect(() => {
    // we want this to run everytime the page loads, so both the timeline and event cards scroll based on the startTime
    props.setScrollerObj('')

    // we also need to trigger a re-render when the component mounts
    // in which case, our scrollRef already has a value, so the time strip size/position can be calculated properly
    setInitialScrollRef(scrollRef.current)

  // eslint-disable-next-line
  }, [])

  useEffect(() => {
    // set the topmost card based on startTime if we are scrolling in the event card side
    if (props.scrollerObj !== 'timeline' && scrollRef.current) {
      let children = scrollRef.current.children[0].children
      let topChild = null

      for (let i = 0; i < children.length; i++) {
        if (parseInt(children[i].dataset.time) >= parseInt(props.startTime)) {
          topChild = children[i]
          break
        }
      }

      topChild && scrollRef.current.scrollTo({top: topChild.offsetTop, behavior: 'smooth'})
    }
  }, [props.startTime, props.scrollerObj])

  const calculateEventSize = (seconds) => {
    if (!scrollRef.current) return 0

    const containerSize = scrollRef.current.clientHeight * 6 // the timeline shows a 4 hour range but can scroll the whole 24 hour range
    const unitSize = containerSize / 86400 // let's divide the entire container size (in px) by 86400 (which is the number of seconds in 1 day)
    const size = seconds * unitSize // then multiply the event's duration with the calculated second size
    return `${size}px`
  }

  const calculateEventPosition = (ts) => {
    if (!scrollRef.current) return

    // format event timestamp to be acceptable by Date() object
    // ts: 2024-01-10T17:01:15.000Z
    const eventTime = new Date(ts.substring(0, 19))
    const startingTime = new Date(`${date} 00:00:00`) // we will always start at 12am so the strips are plot properly on scroll

    const containerSize = scrollRef.current.clientHeight * 6 // the timeline shows a 4 hour range but can scroll the whole 24 hour range
    const unitSize = containerSize / 86400 // let's divide the entire container size (in px) by 86400 (which is the number of seconds in 1 day)
    const timeDiff = (eventTime - startingTime) / 1000 // then get the position based on the events timestamp difference from 12am (value is in milliseconds, so convert to seconds)
    const startPosition = timeDiff * unitSize
    return `${startPosition}px`
  }

  const handleScroll = () => {
    if (props.scrollerObj !== 'timeline' || props.eventsLoading === true) return

    // scroll to the next nearest hour
    if (scrollTimeout !== null) {
      clearTimeout(scrollTimeout)
    }

    const timeout = setTimeout(async () => {
      let children = scrollRef.current.children[0].children
      let scrollTop = scrollRef.current.scrollTop
      let topChild = null

      if (scrollTop === 0) {
        topChild = children[0]
      } else {
        for (let i = 0; i < children.length; i++) {
          if (children[i].offsetTop >= scrollTop) {
            topChild = children[i]
            break
          }
        }
      }

      if (topChild) {
        scrollRef.current.scrollTo({top: topChild.offsetTop, behavior: 'smooth'})
        props.setStartTime(topChild.dataset.time)

        const lastEndTime = props.endTime
        let newEndTime = (parseInt(topChild.dataset.time) + 40000).toString().padStart(6, '0') // + 4 hours
        newEndTime = parseInt(newEndTime) > 240000 ? '240000' : newEndTime // end time should not be more than midnight
        if (parseInt(newEndTime) > parseInt(lastEndTime)) {
          props.setEndTime(newEndTime)
          // we should only search events that we don't have yet, so that would be between the last end time, to the current end time
          await props.getPlaybackEvents(props.token, props.searchFilters.date, props.cameras, props.searchFilters.camera, props.searchFilters.eventTags, lastEndTime, newEndTime, props.events)
        }
      }
    }, 200)

    setScrollTimeout(timeout)
  }

  return (
    <div title="Scroll to view other events" style={{...styles.container, ...(props.eventsLoading && {cursor: 'wait'})}}>
      <div ref={scrollRef} onWheel={() => props.setScrollerObj('timeline')} onScroll={handleScroll} style={styles.timeline}>
        <div style={styles.timeContainer}>
          {hourList.map((h, _) => (
            <div key={h.time} style={{...styles.time, top: calculateEventPosition(`${date} ${h.ts}`)}} data-time={h.time}>{h.label}</div>
          ))}
        </div>
        <div style={styles.eventContainer}>
          {props.filteredEvents.map((ev, _) => (
            !props.eventsLoading
            ? <div key={ev.bID} data-ts={ev.convertedTS} onClick={() => props.setSelectedEvent(ev)}
                style={{...styles.event, height: calculateEventSize(ev.bDuration), top: calculateEventPosition(ev.convertedTS),
                  backgroundColor: ev.sTags.split(',').includes('vehicle') && ev.sTags.split(',').includes('person') ? 'blue'
                    : ev.sTags.split(',').includes('vehicle') ? 'dodgerblue'
                      : ev.sTags.split(',').includes('person') ? 'lightseagreen' : 'rgba(255, 255, 255, 0.4)'
                }}>
              </div>
            : null
          ))}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = state => {
  const { token } = state.auth
  const { cameras } = state.data
  const { date, searchFilters, startTime, endTime, eventsLoading, filteredEvents, events, scrollerObj } = state.playback

  return {
    token,
    cameras,
    date,
    searchFilters,
    startTime,
    endTime,
    eventsLoading,
    filteredEvents,
    events,
    scrollerObj,
  }
}
export default connect(mapStateToProps, {setStartTime, setEndTime, getPlaybackEvents, setScrollerObj, setSelectedEvent})(Timeline)

const styles = {
  container: {
    position: 'relative',
    width: '3vw',
    minWidth: '3vw',
    overflow: 'hidden',
    cursor: 'ns-resize',
    border: '1px solid red',
    height: 'calc(85vh - 4vmin)' // height of the content section - top&bottom padding
  },
  timeline: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    width: '5vw',
    height: '100%',
    overflow: 'hidden scroll',
  },
  timeContainer: {
    position: 'absolute',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingLeft: '.1vw',
  },
  time: {
    position: 'absolute',
    zIndex: 10,
  },
  eventContainer: {
    width: '100%',
  },
  event: {
    position: 'absolute',
    width: '100%',
    cursor: 'pointer',
  },
}
